import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { API_URLS, TIMEZONES } from '../../config/Constants';
import api from '../../config/axiosConfig';
import 'react-toastify/dist/ReactToastify.css';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { Modal, ModalTitle, ModalContent, ModalActions } from '../basic';
import { SearchableOpponentDropdown } from '../common';
import { formatIntraSquadGameName, GAME_MODE_PRACTICE, GAME_TYPE_INTRA_SQUAD } from '../../utils/seasonType';

interface Opponent {
  _id: string;
  name: string;
}

interface GameData {
  _id?: string;
  name: string;
  location: string;
  type: string;
  date: string;
  time: string;
  timezone?: string;
  opponentId?: string;
  opponentName?: string;
  seasonId?: string;
}

interface AddGameModalProps {
  onClose: () => void;
  onGameAdded?: () => void;
  editGame?: GameData | null;
  isTournamentMode?: boolean;
  isPracticeMode?: boolean;
}

const gameSchema = z.object({
  opponentId: z.string().optional(),
  name: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
  time: z.string().optional(),
  timezone: z.string().optional(),
  type: z.string().optional(),
  location: z.string().optional(),
  seasonId: z.string().min(1, 'Season is required'),
});

type GameFormData = z.infer<typeof gameSchema>;

const AddGameModal: React.FC<AddGameModalProps> = ({
  onClose,
  onGameAdded,
  editGame,
  isTournamentMode = false,
  isPracticeMode = false,
}) => {
  const clientIdFromSession = sessionManager.getClientId();
  const seasonIdFromSession = sessionManager.getSeasonId();
  const practiceMode = Boolean(isPracticeMode) && !isTournamentMode;

  // Date constraints: prevent picking dates before today for new regular games.
  // Practice games allow past dates so coaches can log mid-week sessions.
  const todayStr = new Date().toISOString().split('T')[0];
  const existingDateStr = editGame?.date ? new Date(editGame.date).toISOString().split('T')[0] : null;
  const minDate = practiceMode
    ? undefined
    : existingDateStr && existingDateStr < todayStr
      ? existingDateStr
      : todayStr;

  const [opponents, setOpponents] = useState<Opponent[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewIntraSquadName, setPreviewIntraSquadName] = useState('Intra Squad (1)');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
    trigger,
  } = useForm<GameFormData>({
    resolver: zodResolver(gameSchema),
    defaultValues: {
      opponentId: '',
      name: '',
      date: practiceMode ? todayStr : '',
      time: practiceMode ? '12:00 PM' : '',
      timezone: 'eastern',
      type: practiceMode ? GAME_TYPE_INTRA_SQUAD : '',
      location: practiceMode ? 'Home' : '',
      seasonId: '',
    },
  });

  useEffect(() => {
    if (seasonIdFromSession) {
      setValue('seasonId', seasonIdFromSession);
    }
  }, [seasonIdFromSession, setValue]);

  const selectedSeasonId = watch('seasonId');
  const practiceDate = watch('date');
  const selectedGameType = watch('type');
  const isTournamentGameMode = Boolean(isTournamentMode);

  useEffect(() => {
    if (!practiceMode || editGame || !practiceDate || !clientIdFromSession || !seasonIdFromSession) return;

    let cancelled = false;
    const loadPreviewName = async () => {
      try {
        const response = await api.get(API_URLS.GET_GAMES, {
          params: {
            clientId: clientIdFromSession,
            seasonId: seasonIdFromSession,
            gameMode: GAME_MODE_PRACTICE,
            page: 1,
            limit: 200,
          },
        });
        const games = response.data?.success ? response.data.data || [] : [];
        const dateKey = String(practiceDate).slice(0, 10);
        const sameDayCount = games.filter((game: GameData) => String(game.date || '').slice(0, 10) === dateKey).length;
        const nextName = formatIntraSquadGameName(practiceDate, sameDayCount + 1);
        if (!cancelled) {
          setPreviewIntraSquadName(nextName);
          setValue('name', nextName, { shouldValidate: true });
        }
      } catch {
        if (!cancelled) {
          const fallback = formatIntraSquadGameName(practiceDate, 1);
          setPreviewIntraSquadName(fallback);
          setValue('name', fallback, { shouldValidate: true });
        }
      }
    };

    loadPreviewName();
    return () => {
      cancelled = true;
    };
  }, [practiceMode, practiceDate, editGame, clientIdFromSession, seasonIdFromSession, setValue]);

  const fetchClientTimezone = useCallback(async () => {
    if (editGame) return;

    try {
      if (clientIdFromSession) {
        const response = await api.get(API_URLS.GET_CLIENT_BY_ID, {
          params: { clientId: clientIdFromSession },
        });

        if (response?.data?.success && response.data.data?.lead?.timezone) {
          const timezone = response.data.data.lead.timezone;
          setValue('timezone', timezone);
        }
      }
    } catch (error) {
      console.warn('Could not fetch client timezone:', error);
    }
  }, [clientIdFromSession, setValue, editGame]);

  const fetchOpponents = useCallback(async () => {
    if (practiceMode) return;
    try {
      setLoading(true);
      const response = await api.get(API_URLS.GET_OPPONENTS, {
        params: clientIdFromSession ? { clientId: clientIdFromSession } : {},
      });

      if (response?.data?.success) {
        setOpponents(response.data.data || []);
      } else {
        showErrorMsg(response.data.message || 'Failed to fetch opponents');
      }
    } catch (error) {
      showErrorMsg('Failed to fetch opponents.');
    } finally {
      setLoading(false);
    }
  }, [clientIdFromSession, practiceMode]);

  useEffect(() => {
    if (selectedSeasonId && !practiceMode) {
      fetchOpponents();
    }
  }, [selectedSeasonId, fetchOpponents, practiceMode]);

  useEffect(() => {
    fetchClientTimezone();
  }, [fetchClientTimezone]);

  useEffect(() => {
    if (editGame) {
      reset({
        opponentId: editGame.opponentId || '',
        name: editGame.name || '',
        date: editGame.date ? new Date(editGame.date).toISOString().split('T')[0] : '',
        time: editGame.time || '',
        timezone: editGame.timezone?.toLowerCase() || 'eastern',
        type: editGame.type || '',
        location: editGame.location || '',
        seasonId: editGame.seasonId || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editGame, reset]);

  const handleAddNewOpponent = async (opponentName: string) => {
    try {
      setLoading(true);
      const response = await api.post(API_URLS.ADD_OPPONENT, {
        name: opponentName,
        clientId: clientIdFromSession,
      });

      if (response.data.success) {
        const newOpponent = response.data.data;
        setOpponents((prev) => [...prev, newOpponent]);
        setValue('opponentId', newOpponent._id, { shouldValidate: true });
        setValue('name', newOpponent.name, { shouldValidate: true });
        await trigger('opponentId');
        showSuccessMsg('Opponent added successfully!');
      } else {
        showErrorMsg(response.data.message || 'Failed to add opponent');
      }
    } catch (error) {
      showErrorMsg('Failed to add opponent.');
    } finally {
      setLoading(false);
    }
  };

  const selectedOpponentId = watch('opponentId');
  useEffect(() => {
    if (practiceMode || !selectedOpponentId) return;
    const opp = opponents.find((o) => o._id === selectedOpponentId);
    if (opp) {
      setValue('name', opp.name, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOpponentId, opponents, practiceMode]);

  const handleOpponentChange = async (opponentId: string, opponentName?: string) => {
    setValue('opponentId', opponentId, { shouldValidate: true });
    if (opponentName) {
      setValue('name', opponentName, { shouldValidate: true });
    }
    await trigger('opponentId');
  };

  const handleClose = () => {
    if (!loading && !isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (data: GameFormData) => {
    const token = sessionManager.getToken();
    if (!token) {
      showErrorMsg('Authentication token not found. Please log in again.');
      return;
    }

    try {
      setLoading(true);

      if (practiceMode) {
        const submitData: any = {
          clientId: clientIdFromSession,
          seasonId: data.seasonId,
          date: data.date,
          type: GAME_TYPE_INTRA_SQUAD,
          practiceMode: true,
          name: editGame ? data.name?.trim() || editGame.name : previewIntraSquadName,
          time: data.time || '12:00 PM',
          timezone: data.timezone || 'eastern',
          location: 'Home',
        };

        let response;
        if (editGame && editGame._id) {
          response = await api.put(API_URLS.UPDATE_GAME, { ...submitData, _id: editGame._id });
        } else {
          response = await api.post(API_URLS.ADD_GAME, submitData);
        }

        if (response.data.success) {
          showSuccessMsg(editGame ? 'Practice updated successfully!' : 'Practice added successfully!');
          onGameAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to save practice');
        }
        return;
      }

      if (!data.opponentId) {
        showErrorMsg('Opponent is required');
        setLoading(false);
        return;
      }
      if (!data.name?.trim()) {
        showErrorMsg('Game name is required');
        setLoading(false);
        return;
      }
      if (!data.time) {
        showErrorMsg('Time is required');
        setLoading(false);
        return;
      }
      if (!data.timezone) {
        showErrorMsg('Timezone is required');
        setLoading(false);
        return;
      }
      if (!isTournamentGameMode && !data.type) {
        showErrorMsg('Game type is required');
        setLoading(false);
        return;
      }
      if (!isTournamentGameMode && !data.location) {
        showErrorMsg('Location is required');
        setLoading(false);
        return;
      }

      let response;
      if (editGame && editGame._id) {
        const updateData: any = {
          ...data,
          _id: editGame._id,
          clientId: clientIdFromSession,
        };
        if (isTournamentGameMode) {
          delete updateData.type;
          delete updateData.location;
        }
        response = await api.put(API_URLS.UPDATE_GAME, updateData);
        if (response.data.success) {
          showSuccessMsg('Game updated successfully!');
          onGameAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to update game');
        }
      } else {
        const submitData: any = {
          ...data,
          clientId: clientIdFromSession,
        };
        if (isTournamentGameMode) {
          delete submitData.type;
          delete submitData.location;
        }
        response = await api.post(API_URLS.ADD_GAME, submitData);
        if (response.data.success) {
          showSuccessMsg('Game added successfully!');
          onGameAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to add game');
        }
      }
    } catch (error: any) {
      console.error('Error adding/updating game:', error);
      const data = error?.response?.data;
      const msg =
        (data && typeof data === 'object' && data.message) ||
        (typeof data === 'string' ? data : null) ||
        error?.message ||
        'Something went wrong. Please try again.';
      showErrorMsg(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} maxWidth="md" isLoading={loading || isSubmitting}>
      <ModalTitle>
        {editGame
          ? practiceMode
            ? 'Edit Intra Squad Game'
            : 'Edit Game'
          : practiceMode
            ? 'Add Intra Squad Game'
            : 'Add Game'}
      </ModalTitle>

      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)} id="game-form">
          {practiceMode ? (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Practice (Intra Squad) only needs a date. Multiple games on the same day are named Intra Squad (1),
                Intra Squad (2), and so on. You can rename the game later.
              </p>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Date *</label>
                <input
                  type="date"
                  {...register('date')}
                  min={minDate}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                    errors.date ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || isSubmitting}
                />
                {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date.message}</p>}
              </div>
              {editGame ? (
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-1">Rename Game</label>
                  <input
                    type="text"
                    {...register('name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                    disabled={loading || isSubmitting}
                    placeholder="Intra Squad (1)"
                  />
                </div>
              ) : (
                practiceDate && (
                  <div className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-950">
                    Name: <span className="font-semibold">{previewIntraSquadName}</span>
                  </div>
                )
              )}
            </>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Opponent *</label>
                <SearchableOpponentDropdown
                  opponents={opponents}
                  value={watch('opponentId') || ''}
                  onChange={handleOpponentChange}
                  onAddNewOpponent={handleAddNewOpponent}
                  error={errors.opponentId?.message}
                  disabled={loading || isSubmitting}
                  placeholder="Search or select opponent..."
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Date *</label>
                <input
                  type="date"
                  {...register('date')}
                  min={minDate}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.date ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || isSubmitting}
                />
                {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date.message}</p>}
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Time *</label>
                <select
                  {...register('time')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.time ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || isSubmitting}
                >
                  <option value="">--:-- --</option>
                  {Array.from({ length: (23 - 6 + 1) * 4 }, (_, i) => {
                    const totalMinutes = 6 * 60 + i * 15;
                    const hours24 = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
                    const ampm = hours24 < 12 ? 'AM' : 'PM';
                    const label = `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
                    return (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                {errors.time && <p className="text-red-500 text-sm mt-1">{errors.time.message}</p>}
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Timezone *</label>
                <select
                  {...register('timezone')}
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.timezone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || isSubmitting}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                {errors.timezone && <p className="text-red-500 text-sm mt-1">{errors.timezone.message}</p>}
              </div>
              {!isTournamentGameMode && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-1">Type *</label>
                  <select
                    {...register('type')}
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.type ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading || isSubmitting}
                  >
                    <option value="">Select Type</option>
                    <option value="Scrimmage">Scrimmage</option>
                    <option value="Regular">Regular</option>
                    <option value="Conference/League">Conference/League</option>
                    <option value="Playoff">Playoff</option>
                    <option value="Championship">Championship</option>
                  </select>
                  {selectedGameType === 'Scrimmage' && (
                    <p className="text-sm text-gray-600 mt-1">
                      Scrimmage - Data do not aggregate in Seasons Analytics.
                    </p>
                  )}
                  {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>}
                </div>
              )}
              {!isTournamentGameMode && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-1">Location *</label>
                  <select
                    {...register('location')}
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading || isSubmitting}
                  >
                    <option value="">Select Location</option>
                    <option value="Home">Home</option>
                    <option value="Away">Away</option>
                    <option value="Neutral">Neutral</option>
                  </select>
                  {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location.message}</p>}
                </div>
              )}
            </>
          )}
        </form>
      </ModalContent>

      <ModalActions>
        <button
          type="button"
          onClick={handleClose}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
          disabled={loading || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="game-form"
          className={`${
            practiceMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-green-600 hover:bg-green-700'
          } text-white px-4 py-2 rounded transition-colors`}
          disabled={loading || isSubmitting}
        >
          {loading || isSubmitting
            ? editGame
              ? 'Updating...'
              : 'Creating...'
            : editGame
              ? 'Update Game' : 'Create Game'}
        </button>
      </ModalActions>
    </Modal>
  );
};

export default AddGameModal;
